/**
 * Supabase DB 쿼리 레이어
 * snake_case(DB) ↔ camelCase(UI) 자동 변환
 */
import { supabase } from './supabase';
import { toCamel } from '../utils/helpers';

// ---------- 회원 ----------
export async function dbAddMember(gymId, data) {
  const { birthDate, createdAt, ...rest } = data;
  const { data: row, error } = await supabase
    .from('members')
    .insert({ ...rest, gym_id: gymId, birth_date: birthDate || null })
    .select()
    .single();
  if (error) throw error;
  return toCamel(row);
}

export async function dbUpdateMember(id, data) {
  const { birthDate, createdAt, gymId, ...rest } = data;
  const { error } = await supabase
    .from('members')
    .update({ ...rest, ...(birthDate !== undefined && { birth_date: birthDate }) })
    .eq('id', id);
  if (error) throw error;
}

export async function dbDeleteMember(id) {
  const { error } = await supabase.from('members').delete().eq('id', id);
  if (error) throw error;
}

// ---------- 회원권 상품 ----------
export async function dbAddMembershipProduct(gymId, data) {
  const { totalCount, days, ...rest } = data;
  const { data: row, error } = await supabase
    .from('membership_products')
    .insert({ ...rest, gym_id: gymId, duration_days: days, total_count: totalCount || null })
    .select()
    .single();
  if (error) throw error;
  return { ...toCamel(row), days: row.duration_days };
}

export async function dbUpdateMembershipProduct(id, data) {
  const { totalCount, days, gymId, ...rest } = data;
  const updateObj = { ...rest };
  if (days !== undefined) updateObj.duration_days = days;
  if (totalCount !== undefined) updateObj.total_count = totalCount;
  const { error } = await supabase
    .from('membership_products')
    .update(updateObj)
    .eq('id', id);
  if (error) throw error;
}

export async function dbDeleteMembershipProduct(id) {
  const { error } = await supabase.from('membership_products').delete().eq('id', id);
  if (error) throw error;
}

// ---------- 회원권 ----------
export async function dbAddMembership(gymId, data) {
  const { memberId, productId, productName, startDate, endDate, freezeHistory, transferHistory, paymentMethod, cashReceipt, createdAt, ...rest } = data;
  const { data: row, error } = await supabase
    .from('memberships')
    .insert({
      ...rest,
      gym_id: gymId,
      member_id: memberId,
      product_id: productId ?? null,
      product_name: productName ?? null,
      start_date: startDate ?? null,
      end_date: endDate ?? null,
      freeze_history: freezeHistory ?? [],
      transfer_history: transferHistory ?? [],
      payment_method: paymentMethod ?? null,
      cash_receipt: cashReceipt ?? null,
    })
    .select()
    .single();
  if (error) throw error;
  return toCamel(row);
}

export async function dbUpdateMembership(id, data) {
  const { memberId, productId, productName, startDate, endDate, frozenAt, freezeReason, freezeHistory, transferHistory, paymentMethod, cashReceipt, gymId, createdAt, ...rest } = data;
  const updateObj = { ...rest };
  if (memberId !== undefined) updateObj.member_id = memberId;
  if (productId !== undefined) updateObj.product_id = productId;
  if (productName !== undefined) updateObj.product_name = productName;
  if (startDate !== undefined) updateObj.start_date = startDate;
  if (endDate !== undefined) updateObj.end_date = endDate;
  if (frozenAt !== undefined) updateObj.frozen_at = frozenAt;
  if (freezeReason !== undefined) updateObj.freeze_reason = freezeReason;
  if (freezeHistory !== undefined) updateObj.freeze_history = freezeHistory;
  if (transferHistory !== undefined) updateObj.transfer_history = transferHistory;
  if (paymentMethod !== undefined) updateObj.payment_method = paymentMethod;
  if (cashReceipt !== undefined) updateObj.cash_receipt = cashReceipt;
  const { error } = await supabase.from('memberships').update(updateObj).eq('id', id);
  if (error) throw error;
}

export async function dbDeleteMembership(id) {
  const { error } = await supabase.from('memberships').delete().eq('id', id);
  if (error) throw error;
}

// ---------- 출석 ----------
export async function dbAddAttendance(gymId, memberId, date, time) {
  const { data: row, error } = await supabase
    .from('attendances')
    .insert({ gym_id: gymId, member_id: memberId, date, time })
    .select()
    .single();
  if (error) throw error;
  return toCamel(row);
}

export async function dbDeleteAttendance(id) {
  const { error } = await supabase.from('attendances').delete().eq('id', id);
  if (error) throw error;
}

// ---------- 트레이너 ----------
export async function dbAddTrainer(gymId, data) {
  const { createdAt, ...rest } = data;
  const { data: row, error } = await supabase
    .from('trainers')
    .insert({ ...rest, gym_id: gymId })
    .select()
    .single();
  if (error) throw error;
  return toCamel(row);
}

export async function dbUpdateTrainer(id, data) {
  const { gymId, createdAt, ...rest } = data;
  const { error } = await supabase.from('trainers').update(rest).eq('id', id);
  if (error) throw error;
}

export async function dbDeleteTrainer(id) {
  const { error } = await supabase.from('trainers').delete().eq('id', id);
  if (error) throw error;
}

// ---------- PT 상품 ----------
export async function dbAddPtProduct(gymId, data) {
  const { data: row, error } = await supabase
    .from('pt_products')
    .insert({ ...data, gym_id: gymId })
    .select()
    .single();
  if (error) throw error;
  return toCamel(row);
}

export async function dbUpdatePtProduct(id, data) {
  const { gymId, ...rest } = data;
  const { error } = await supabase.from('pt_products').update(rest).eq('id', id);
  if (error) throw error;
}

export async function dbDeletePtProduct(id) {
  const { error } = await supabase.from('pt_products').delete().eq('id', id);
  if (error) throw error;
}

// ---------- PT 부여 ----------
export async function dbAddPtAssignment(gymId, data) {
  const { memberId, productId, productName, trainerId, trainerName, totalSessions, usedSessions, paymentMethod, cashReceipt, createdAt, ...rest } = data;
  const { data: row, error } = await supabase
    .from('pt_assignments')
    .insert({
      ...rest,
      gym_id: gymId,
      member_id: memberId,
      product_id: productId ?? null,
      product_name: productName ?? null,
      trainer_id: trainerId ?? null,
      trainer_name: trainerName ?? null,
      total_sessions: totalSessions,
      used_sessions: usedSessions ?? 0,
      payment_method: paymentMethod ?? null,
      cash_receipt: cashReceipt ?? null,
    })
    .select()
    .single();
  if (error) throw error;
  return toCamel(row);
}

export async function dbUpdatePtAssignment(id, data) {
  const { memberId, productId, productName, trainerId, trainerName, totalSessions, usedSessions, paymentMethod, cashReceipt, gymId, createdAt, ...rest } = data;
  const updateObj = { ...rest };
  if (memberId !== undefined) updateObj.member_id = memberId;
  if (productId !== undefined) updateObj.product_id = productId;
  if (productName !== undefined) updateObj.product_name = productName;
  if (trainerId !== undefined) updateObj.trainer_id = trainerId;
  if (trainerName !== undefined) updateObj.trainer_name = trainerName;
  if (totalSessions !== undefined) updateObj.total_sessions = totalSessions;
  if (usedSessions !== undefined) updateObj.used_sessions = usedSessions;
  if (paymentMethod !== undefined) updateObj.payment_method = paymentMethod;
  if (cashReceipt !== undefined) updateObj.cash_receipt = cashReceipt;
  const { error } = await supabase.from('pt_assignments').update(updateObj).eq('id', id);
  if (error) throw error;
}

// ---------- PT 일지 ----------
export async function dbAddPtLog(gymId, data) {
  const { ptAssignmentId, memberId, trainerId, trainerName, sessionNumber, ...rest } = data;
  const { data: row, error } = await supabase
    .from('pt_logs')
    .insert({
      ...rest,
      gym_id: gymId,
      pt_assignment_id: ptAssignmentId,
      member_id: memberId,
      trainer_id: trainerId ?? null,
      trainer_name: trainerName ?? null,
      session_number: sessionNumber ?? null,
    })
    .select()
    .single();
  if (error) throw error;
  return toCamel(row);
}

export async function dbUpdatePtLog(id, data) {
  const { content, memo } = data;
  const patch = {};
  if (content !== undefined) patch.content = content;
  if (memo !== undefined) patch.memo = memo;
  const { error } = await supabase.from('pt_logs').update(patch).eq('id', id);
  if (error) throw error;
}

export async function dbDeletePtLog(id) {
  const { error } = await supabase.from('pt_logs').delete().eq('id', id);
  if (error) throw error;
}

// ---------- 락커 ----------
export async function dbAddLockers(gymId, dataArray) {
  const rows = dataArray.map(({ memberId, memberName, startDate, endDate, deposit, assigneeType, ...rest }) => ({
    ...rest,
    gym_id: gymId,
    member_id: memberId ?? null,
    member_name: memberName ?? null,
    start_date: startDate ?? null,
    end_date: endDate ?? null,
    deposit: deposit ?? 0,
    assignee_type: assigneeType ?? 'member',
  }));
  const { data, error } = await supabase.from('lockers').insert(rows).select();
  if (error) throw error;
  return (data || []).map(toCamel);
}

export async function dbAddLocker(gymId, data) {
  const { memberId, memberName, startDate, endDate, deposit, assigneeType, ...rest } = data;
  const { data: row, error } = await supabase
    .from('lockers')
    .insert({
      ...rest,
      gym_id: gymId,
      member_id: memberId ?? null,
      member_name: memberName ?? null,
      start_date: startDate ?? null,
      end_date: endDate ?? null,
      deposit: deposit ?? 0,
      assignee_type: assigneeType ?? 'member',
    })
    .select()
    .single();
  if (error) throw error;
  return toCamel(row);
}

export async function dbUpdateLocker(id, data) {
  const { memberId, memberName, startDate, endDate, deposit, assigneeType, gymId, ...rest } = data;
  const updateObj = { ...rest };
  if (memberId !== undefined) updateObj.member_id = memberId;
  if (memberName !== undefined) updateObj.member_name = memberName;
  if (startDate !== undefined) updateObj.start_date = startDate;
  if (endDate !== undefined) updateObj.end_date = endDate;
  if (deposit !== undefined) updateObj.deposit = deposit;
  if (assigneeType !== undefined) updateObj.assignee_type = assigneeType;
  const { error } = await supabase.from('lockers').update(updateObj).eq('id', id);
  if (error) throw error;
}

export async function dbDeleteLocker(id) {
  const { error } = await supabase.from('lockers').delete().eq('id', id);
  if (error) throw error;
}

// ---------- 헬스장 설정 ----------
export async function dbUpdateGym(gymId, data) {
  const { businessHours, logoColor, lockerSettings, ...rest } = data;
  const updateObj = { ...rest };
  if (businessHours !== undefined) updateObj.business_hours = businessHours;
  if (logoColor !== undefined) updateObj.logo_color = logoColor;
  if (lockerSettings !== undefined) updateObj.locker_settings = lockerSettings;
  const { error } = await supabase.from('gyms').update(updateObj).eq('id', gymId);
  if (error) throw error;
}

// ---------- 가입 신청 (pending members) ----------
export async function dbApproveMember(id) {
  const { error } = await supabase
    .from('members')
    .update({ status: 'active' })
    .eq('id', id);
  if (error) throw error;
}

export async function dbRejectMember(id) {
  const { error } = await supabase
    .from('members')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ---------- 연장 신청 ----------
export async function dbGetRenewalRequests(gymId) {
  const { data } = await supabase
    .from('renewal_requests')
    .select('*, members(name, phone)')
    .eq('gym_id', gymId)
    .order('created_at', { ascending: false });
  return (data || []).map(r => ({
    ...toCamel(r),
    memberName: r.members?.name,
    memberPhone: r.members?.phone,
  }));
}

export async function dbUpdateRenewalRequest(id, data) {
  const { processedAt, processedNote, ...rest } = data;
  const updateObj = { ...rest };
  if (processedAt !== undefined) updateObj.processed_at = processedAt;
  if (processedNote !== undefined) updateObj.processed_note = processedNote;
  const { error } = await supabase.from('renewal_requests').update(updateObj).eq('id', id);
  if (error) throw error;
}

// ---------- 정보 수정 신청 ----------
export async function dbGetProfileEditRequests(gymId) {
  const { data } = await supabase
    .from('profile_edit_requests')
    .select('*, members(name, phone)')
    .eq('gym_id', gymId)
    .order('created_at', { ascending: false });
  return (data || []).map(r => ({
    ...toCamel(r),
    memberName: r.members?.name,
    memberPhone: r.members?.phone,
  }));
}

export async function dbUpdateProfileEditRequest(id, data) {
  const { processedAt, processedNote, ...rest } = data;
  const updateObj = { ...rest };
  if (processedAt !== undefined) updateObj.processed_at = processedAt;
  if (processedNote !== undefined) updateObj.processed_note = processedNote;
  const { error } = await supabase.from('profile_edit_requests').update(updateObj).eq('id', id);
  if (error) throw error;
}
